/**
 * Right-pane editor for AI-structured sections.
 *
 * Lets the user manually edit Key Takeaways, Agreements / Decisions, and Next Steps
 * (including optional responsible person and deadline). No speech-to-text here.
 */

import { Button, Flex, Input, Space, Typography, theme } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { StructuredNoteSections } from './types';

const { Text } = Typography;

interface StructuredNoteEditorProps {
  value: StructuredNoteSections;
  onChange: (value: StructuredNoteSections) => void;
  disabled?: boolean;
}

/**
 * Editable list of simple text bullets with add / remove controls.
 */
function TextItemList({
  items,
  onChange,
  addLabel,
  placeholder,
  disabled,
}: {
  items: Array<{ text: string }>;
  onChange: (items: Array<{ text: string }>) => void;
  addLabel: string;
  placeholder: string;
  disabled?: boolean;
}) {
  const { token } = theme.useToken();

  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      {items.map((item, index) => (
        <Flex key={index} gap={token.marginXS} align="start">
          <Input.TextArea
            value={item.text}
            onChange={(event) => {
              const next = [...items];
              next[index] = { text: event.target.value };
              onChange(next);
            }}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={disabled}
            style={{ flex: 1 }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={addLabel}
            disabled={disabled}
            onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
          />
        </Flex>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={() => onChange([...items, { text: '' }])}
        block
      >
        {addLabel}
      </Button>
    </Space>
  );
}

/**
 * Full structured-note form for the three section groups.
 */
export function StructuredNoteEditor({ value, onChange, disabled }: StructuredNoteEditorProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {t('structuredNote.sections.keyTakeaways')}
        </Text>
        <TextItemList
          items={value.keyTakeaways}
          onChange={(items) => onChange({ ...value, keyTakeaways: items })}
          addLabel={t('structuredNote.addItem')}
          placeholder={t('structuredNote.placeholders.keyTakeaway')}
          disabled={disabled}
        />
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {t('structuredNote.sections.agreementsDecisions')}
        </Text>
        <TextItemList
          items={value.agreementsDecisions}
          onChange={(items) => onChange({ ...value, agreementsDecisions: items })}
          addLabel={t('structuredNote.addItem')}
          placeholder={t('structuredNote.placeholders.agreement')}
          disabled={disabled}
        />
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {t('structuredNote.sections.nextSteps')}
        </Text>
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          {value.nextSteps.map((step, index) => (
            <Flex key={index} gap={token.marginXS} align="start" wrap="wrap">
              <Input
                value={step.task}
                onChange={(event) => {
                  const next = [...value.nextSteps];
                  next[index] = { ...next[index], task: event.target.value };
                  onChange({ ...value, nextSteps: next });
                }}
                placeholder={t('structuredNote.placeholders.task')}
                disabled={disabled}
                style={{ flex: '1 1 180px' }}
              />
              <Input
                value={step.responsible ?? ''}
                onChange={(event) => {
                  const next = [...value.nextSteps];
                  next[index] = {
                    ...next[index],
                    responsible: event.target.value.trim() ? event.target.value : null,
                  };
                  onChange({ ...value, nextSteps: next });
                }}
                placeholder={t('structuredNote.placeholders.responsible')}
                disabled={disabled}
                style={{ flex: '1 1 140px' }}
              />
              <Input
                value={step.deadline ?? ''}
                onChange={(event) => {
                  const next = [...value.nextSteps];
                  next[index] = {
                    ...next[index],
                    deadline: event.target.value.trim() ? event.target.value : null,
                  };
                  onChange({ ...value, nextSteps: next });
                }}
                placeholder={t('structuredNote.placeholders.deadline')}
                disabled={disabled}
                style={{ flex: '1 1 120px' }}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...value,
                    nextSteps: value.nextSteps.filter((_, itemIndex) => itemIndex !== index),
                  })
                }
              />
            </Flex>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={() =>
              onChange({
                ...value,
                nextSteps: [...value.nextSteps, { task: '', responsible: null, deadline: null }],
              })
            }
            block
          >
            {t('structuredNote.addNextStep')}
          </Button>
        </Space>
      </div>
    </Space>
  );
}
